// src/components/doctorsJournal/JournalPasswordSetup.js
import React, { useState } from 'react';
import { Lock, AlertTriangle, CheckCircle2, Check } from 'lucide-react';

export const JournalPasswordSetup = ({ isOpen, onSetup }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const passwordRequirements = [
    { id: 'length', label: 'לפחות 8 תווים', test: (pwd) => pwd.length >= 8 },
    { id: 'uppercase', label: 'אות גדולה באנגלית', test: (pwd) => /[A-Z]/.test(pwd) },
    { id: 'lowercase', label: 'אות קטנה באנגלית', test: (pwd) => /[a-z]/.test(pwd) },
    { id: 'digit', label: 'ספרה אחת לפחות', test: (pwd) => /\d/.test(pwd) }
  ];

  const isPasswordStrong = passwordRequirements.every(req => req.test(password));

  const validatePassword = () => {
    if (!isPasswordStrong) {
      setError('הסיסמה לא עומדת בכל הדרישות');
      return false;
    }
    if (password !== confirmPassword) {
      setError('הסיסמאות אינן תואמות');
      return false;
    }
    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (validatePassword()) {
      onSetup(password);
      setPassword('');
      setConfirmPassword('');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-md mx-4">
        <div className="bg-white rounded-2xl shadow-2xl border border-pink-100 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-br from-pink-50 to-purple-50 px-6 py-8 text-center border-b border-pink-100">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-pink-500 rounded-full flex items-center justify-center shadow-lg">
                <Lock className="w-8 h-8 text-white" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">הגדרת סיסמת יומן</h2>
            <p className="text-sm text-gray-600">צור סיסמה להצפנת רשומות היומן</p>
          </div>

          {/* Warning Alert */}
          <div className="mx-6 mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-semibold text-amber-900 mb-1">אזהרה חשובה</p>
                <p className="text-amber-800">אובדן הסיסמה יגרום לאובדן כל הנתונים המוצפנים ללא אפשרות לשחזור</p>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-6 py-2 pb-6 space-y-5">
            <div className="mt-3 space-y-2">
              {passwordRequirements.map(req => {
                const isMet = req.test(password);
                return (
                  <div key={req.id} className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center ${isMet ? 'bg-green-500' : 'bg-gray-300'
                      }`}>
                      <Check className="w-3 h-3 text-white" />
                    </div>
                    <span className={`text-xs ${isMet ? 'text-green-600' : 'text-gray-500'}`}>
                      {req.label}
                    </span>
                  </div>
                );
              })}
            </div>
            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                סיסמה חדשה
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all"
                placeholder="הזן סיסמה (לפחות 8 תווים)"
                required
              />

            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                אימות סיסמה
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all"
                placeholder="הזן סיסמה שנית"
                required
              />
              {confirmPassword && password === confirmPassword && (
                <div className="flex items-center gap-2 mt-2 text-green-600">
                  <CheckCircle2 className="w-4 h-4" />
                  <span className="text-xs font-medium">הסיסמאות תואמות</span>
                </div>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-sm text-red-800 text-center">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={!isPasswordStrong || !confirmPassword || password !== confirmPassword}
              className="w-full py-3 bg-gradient-to-l from-pink-500 to-purple-500 text-white font-semibold rounded-xl hover:from-pink-600 hover:to-purple-600 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:from-pink-500 disabled:hover:to-purple-500"
            >
              יצירת סיסמה והפעלת יומן
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};